import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;

// Cookie-based sessions:
//   - httpOnly session cookies set by the backend on /auth/login
//   - we read the double-submit CSRF value either from the login response (stored in-memory)
//     OR from the lwco_csrf cookie which is NOT httpOnly on purpose.
// We NEVER store a bearer token in localStorage/sessionStorage.
let _csrf = "";
export function setCsrf(v) { _csrf = v || ""; }
export function getCsrf() {
  if (_csrf) return _csrf;
  // Fallback: pull from readable cookie set by the backend
  const m = document.cookie.match(/(?:^|;\s*)lwco_csrf=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : "";
}

const api = axios.create({ baseURL: API, withCredentials: true });

// Plain axios instance (no interceptors) for the refresh call itself,
// so a 401 from /auth/refresh can never re-enter the response interceptor
// and deadlock on the shared _refreshing promise. Exported so AuthContext
// can hit /auth/refresh directly on its /auth/me 401 without recursion.
export const bare = axios.create({ baseURL: API, withCredentials: true });

// Prune any legacy bearer tokens on module load so old sessions can never resurrect.
try { localStorage.removeItem("lwco_token"); sessionStorage.removeItem("lwco_token"); } catch {}

// Any URL that is itself part of the auth lifecycle must bypass the auto-refresh
// branch — otherwise /auth/me's 401 triggers a refresh whose own 401 would recurse.
const AUTH_URLS = ["/auth/login", "/auth/refresh", "/auth/logout",
                   "/auth/logout-all", "/auth/me", "/auth/reauth"];
function isAuthUrl(url) {
  if (!url) return false;
  return AUTH_URLS.some(u => url.includes(u));
}

api.interceptors.request.use((config) => {
  const m = (config.method || "get").toLowerCase();
  if (["post", "put", "patch", "delete"].includes(m)) {
    const t = getCsrf();
    if (t) config.headers["X-CSRF-Token"] = t;
  }
  return config;
});

// 401 handling: try a single refresh. If that fails, drop to /login.
let _refreshing = null;
api.interceptors.response.use(
  (r) => {
    const m = (r.config?.method || "get").toLowerCase();
    if (m !== "get" && /\/leave/.test(r.config?.url || "")) window.dispatchEvent(new Event("leave-changed"));
    return r;
  },
  async (err) => {
    const original = err?.config;
    const status = err?.response?.status;
    const url = original?.url || "";
    if (status !== 401 || !original || original.__retried || isAuthUrl(url)) {
      return Promise.reject(err);
    }
    original.__retried = true;
    try {
      if (!_refreshing) {
        _refreshing = bare.post("/auth/refresh").then(r => {
          if (r?.data?.csrf_token) setCsrf(r.data.csrf_token);
          return r;
        }).finally(() => { _refreshing = null; });
      }
      await _refreshing;
      return api(original);
    } catch {
      setCsrf("");
      const path = window.location.pathname;
      if (!path.startsWith("/login")
          && !path.startsWith("/v/")
          && !path.startsWith("/d/")
          && !path.startsWith("/qr/")
          && !path.startsWith("/site")) {
        window.location.href = "/login";
      }
      return Promise.reject(err);
    }
  }
);

export default api;

export function fmtNZD(n) {
  if (n === undefined || n === null || isNaN(n)) return "$0.00";
  return "$" + Number(n).toLocaleString("en-NZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function nzISODate(d = new Date()) {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Pacific/Auckland", year: "numeric", month: "2-digit", day: "2-digit" }).format(d);
}

export function fmtDate(iso) {
  if (!iso) return "";
  if (typeof iso === "string" && /^\d{4}-\d{2}-\d{2}T/.test(iso)) {
    return new Date(iso).toLocaleDateString("en-NZ", { timeZone: "Pacific/Auckland", day: "2-digit", month: "2-digit", year: "numeric" });
  }
  const s = typeof iso === "string" ? iso.slice(0, 10) : iso;
  const parts = s.split("-");
  if (parts.length !== 3) return s;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
}

export function toISODate(dmy) {
  if (!dmy) return "";
  if (dmy.includes("-")) return dmy;
  const [dd, mm, yyyy] = dmy.split("/");
  return `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
}

export function todayDMY() {
  const [y, m, d] = nzISODate().split("-");
  return `${d}/${m}/${y}`;
}
